/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { type ReactNode } from 'react';

import { jsx } from '@compiled/react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Flex, Grid, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { ApplicationModes } from '../../constants/application-modes';
import {
	type ResultClickedCallback,
	type ResultClickedCallbackParams,
} from '../../constants/types';
import { isAuxClick } from '../../utils/clicks';
import { CopyLinkButton } from '../copy-link-button';
import { Link } from '../link-component';
import { SummarizeInChatButton } from '../summarize-in-chat-button';

import { isVisited } from './is-visited';
import { addVisitedLink, getVisitedLinks } from './utils';

const headingAreaStyles = xcss({
	gridArea: 'heading',
	font: token('font.body.large'),
	minWidth: '0px',
});

const titleStyles = xcss({
	display: 'inline-flex',
	overflow: 'hidden',
});

const titleTextStyles = xcss({
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
	font: 'inherit',
});

const titleTextLinkStyles = xcss({
	color: 'color.link',
	':hover': {
		color: 'color.link',
	},
});

const visitedLinkStyles = xcss({
	textDecoration: 'none',
	':visited:active': {
		color: 'color.link.visited.pressed',
	},
	':visited': {
		color: 'color.link.visited',
	},
});

const visitedLinkPatchStyles = xcss({
	color: 'color.link.visited',
	':active': {
		color: 'color.link.visited.pressed',
	},
});

const titleMetadataStyles = xcss({
	display: 'flex',
	gap: 'space.100',
	alignItems: 'center',
	justifyContent: 'end',
	height: token('space.300'),
});

type SearchResultHeaderProps = {
	id?: string;
	title: ReactNode;
	url?: string;
	isThirdParty?: boolean;
	appMode?: ApplicationModes;
	isHovered: boolean;
	titleMetadata?: ReactNode;
	showSummarizeInChatButton?: boolean;
	/** Used to determine if the result has any related links */
	hasRelatedLinks?: boolean;
	/** Called when the result is clicked */
	onResultClicked?: ResultClickedCallback;
	/** Called when the result context menu is opened */
	onResultContextMenu?: VoidFunction;
	/** Called when the copy link button is clicked */
	onCopyLinkButtonClicked?: VoidFunction;
	/** Called when the summarize in chat button is clicked */
	onSummarizeInChatButtonClicked?: VoidFunction;
};

export const SearchResultTitle: ({
	id,
	title,
	url,
	isThirdParty,
	appMode,
	isHovered,
	titleMetadata,
	showSummarizeInChatButton,
	hasRelatedLinks,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
}: SearchResultHeaderProps) => JSX.Element = ({
	id,
	title,
	url,
	isThirdParty = false,
	appMode,
	isHovered,
	titleMetadata,
	showSummarizeInChatButton = false,
	hasRelatedLinks,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
}: SearchResultHeaderProps) => {
	const isRovoMode = appMode === ApplicationModes.Rovo;

	/**
	 * A dynamic search_id query param and queryHash is appended to the document URL for 1P search results.
	 * As a result we are unable to use the browser's visited links history to determine if a link has been visited.
	 * This is workaround until we have a better solution.
	 */
	const visitedLinks = getVisitedLinks();
	const isVisitedLink = id ? isVisited(visitedLinks, id) : false;

	const onClick = ({
		isAuxClick = false,
		isSummarizeClick = false,
		hasRelatedLinks = false,
	}: {
		isAuxClick?: boolean;
		isSummarizeClick?: boolean;
		hasRelatedLinks?: boolean;
	} = {}) => {
		// Mark the link as visited, but only for normal clicks
		if (id && !isSummarizeClick) {
			addVisitedLink(id);
		}
		onResultClicked?.({
			hasRelatedLinks,
			isAuxClick,
			isSummarizeClick,
		} satisfies ResultClickedCallbackParams);
	};
	return (
		// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
		<Box
			xcss={headingAreaStyles}
			onClick={() => onClick({ hasRelatedLinks })}
			onContextMenu={onResultContextMenu}
			onAuxClick={(e: React.MouseEvent<HTMLDivElement>) => {
				if (isAuxClick(e)) {
					return onClick({
						isAuxClick: true,
						hasRelatedLinks,
					});
				}
			}}
		>
			{id && url ? (
				<Grid templateColumns="minmax(50%, 1fr) min-content" gap="space.400">
					<Flex alignItems="center" gap="space.100">
						<Link
							xcss={[visitedLinkStyles, isVisitedLink && visitedLinkPatchStyles, titleStyles]}
							rel={isThirdParty ? 'noreferrer' : ''}
							target={isThirdParty ? '_blank' : undefined}
							href={url}
							testId="search-page-result-link"
						>
							<Box
								xcss={[
									titleTextStyles,
									titleTextLinkStyles,
									isVisitedLink && visitedLinkPatchStyles,
								]}
								as="h2"
							>
								{title}
							</Box>
						</Link>
						{isHovered && isRovoMode && (
							<Inline space="space.050" alignBlock="center">
								<CopyLinkButton
									showSuccess
									testId={id}
									fullResultUrl={url}
									onClick={onCopyLinkButtonClicked}
								/>
								{showSummarizeInChatButton && (
									<SummarizeInChatButton
										ari={id}
										url={url}
										onClick={() => {
											onSummarizeInChatButtonClicked?.();
											onClick({ isSummarizeClick: true });
										}}
										testId={'search-result-summarize-in-chat'}
									/>
								)}
							</Inline>
						)}
					</Flex>
					{titleMetadata && <Box xcss={titleMetadataStyles}>{titleMetadata}</Box>}
				</Grid>
			) : (
				<Box xcss={titleStyles}>
					<Box xcss={titleTextStyles} as="h2">
						{title}
					</Box>
				</Box>
			)}
		</Box>
	);
};
